import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Parse from "parse";

export const logIn = createAsyncThunk(
  "authentication/logIn",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const res = await Parse.User.logIn(
        payload.usernameValue,
        payload.passwordValue
      );
      // logIn returns the corresponding ParseUser object
      // To verify that this is in fact the current user, `current` can be used
      //   const currentUser = await Parse.User.current();
      //   console.log(loggedInUser === currentUser);
      if (res.error) {
        rejectWithValue(res.error);
      }
      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
export const signUp = createAsyncThunk(
  "authentication/signUp",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const { username, password, email, phone } = payload;
      const res = await Parse.User.signUp(username, password, { email, phone });
      if (res.error) {
        rejectWithValue(res.error);
      }
      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const logOut = createAsyncThunk("authentication/logOut", async () => {
  try {
    await Parse.User.logOut();
    return true;
  } catch (error) {
    alert(`Error! ${error.message}`);
    return false;
  }
});

export const requestPasswordReset = createAsyncThunk(
  "authentication/requestPasswordReset",
  async (email, { getState, rejectWithValue }) => {
    try {
      return await Parse.User.requestPasswordReset(email);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const checkCurrentUserExists = createAsyncThunk(
  "authentication/checkCurrentUser",
  async () => {
    const currentUser = await Parse.User.currentAsync();
    return currentUser;
  }
);
export const getUserRole = createAsyncThunk(
  "authentication/getUserRole",
  async (_, { getState, rejectWithValue }) => {
    try {
      const query = await new Parse.Query(Parse.Role)
        .equalTo("users", Parse.User.current())
        .find();

      return query.length > 0 ? query[0].attributes.name : "ROLE_USER";
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const initialState = {
  currentUser: null,
  userLogInLoading: "IDLE",
  userLogInError: null,
  userLogOutLoading: "IDLE",
  userLogOutError: null,
  requestPasswordResetLoading: "IDLE",
  requestPasswordResetError: null,
  token: null,
  checkIfCurrentUserExistsLoading: "IDLE",
  checkIfCurrentUserExistsError: null,
  signUpUserLoading: "IDLE",
  signUpUserError: null,
  currentUserRole: null,
  getCurrentUserRoleLoading: "IDLE",
  getCurrentUserRoleError: null,
};

export const authenticationSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    clearRoleState: (state, action) => {
      state.currentUserRole = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logIn.pending, (state, action) => {
      state.userLogInLoading = "PENDING";
      state.userLogInError = null;
      state.user = null;
    });
    builder.addCase(logIn.fulfilled, (state, action) => {
      state.userLogInLoading = "SUCCESS";
      state.userLogInError = null;
      state.user = action.payload.attributes;
    });
    builder.addCase(logIn.rejected, (state, action) => {
      state.userLogInLoading = "FAILED";
      const err = {
        code: action.payload.code,
        message: action.payload.message,
      };
      state.userLogInError = err;
      state.currentUser = null;
    });

    builder.addCase(logOut.pending, (state, action) => {
      state.userLogOutLoading = "PENDING";
      state.userLogOutError = null;
    });
    builder.addCase(logOut.fulfilled, (state, action) => {
      state.userLogOutLoading = "SUCCESS";
      state.userLogOutError = null;
    });
    builder.addCase(logOut.rejected, (state, action) => {
      state.userLogOutLoading = "FAILED";
      state.userLogOutError = action.payload;
    });

    builder.addCase(requestPasswordReset.pending, (state, action) => {
      state.requestPasswordResetLoading = "PENDING";
      state.requestPasswordResetError = null;
    });
    builder.addCase(requestPasswordReset.fulfilled, (state, action) => {
      state.requestPasswordResetLoading = "SUCCESS";
      state.requestPasswordResetError = null;
    });
    builder.addCase(requestPasswordReset.rejected, (state, action) => {
      state.requestPasswordResetLoading = "FAILED";
      state.requestPasswordResetError = action.payload;
    });

    builder.addCase(checkCurrentUserExists.pending, (state, action) => {
      state.checkIfCurrentUserExistsLoading = "PENDING";
      state.checkIfCurrentUserExistsError = null;
    });
    builder.addCase(checkCurrentUserExists.fulfilled, (state, action) => {
      state.checkIfCurrentUserExistsLoading = "SUCCESS";
      state.checkIfCurrentUserExistsError = null;
      state.user = action.payload?.attributes;
    });
    builder.addCase(checkCurrentUserExists.rejected, (state, action) => {
      state.checkIfCurrentUserExistsLoading = "FAILED";
      state.checkIfCurrentUserExistsError = action.payload;
    });

    builder.addCase(signUp.pending, (state, action) => {
      state.signUpUserLoading = "PENDING";
      state.signUpUserError = null;
    });
    builder.addCase(signUp.fulfilled, (state, action) => {
      state.signUpUserLoading = "SUCCESS";
      state.signUpUserError = null;
    });
    builder.addCase(signUp.rejected, (state, action) => {
      state.signUpUserLoading = "FAILED";
      const err = {
        code: action.payload.code,
        message: action.payload.message,
      };
      state.signUpUserError = err;
    });

    builder.addCase(getUserRole.pending, (state, action) => {
      state.getCurrentUserRoleLoading = "PENDING";
      state.getCurrentUserRoleError = null;
    });
    builder.addCase(getUserRole.fulfilled, (state, action) => {
      state.getCurrentUserRoleLoading = "SUCCESS";
      state.getCurrentUserRoleError = null;
      state.currentUserRole = action.payload;
    });
    builder.addCase(getUserRole.rejected, (state, action) => {
      state.getCurrentUserRoleLoading = "FAILED";
      const err = {
        code: action.payload.code,
        message: action.payload.message,
      };
      state.getCurrentUserRoleError = err;
    });
  },
});

// this is for dispatch
export const { clearRoleState } = authenticationSlice.actions;

// this is for configureStore
export default authenticationSlice.reducer;
